import {
  createExpoSqliteAdapter,
  useRozeniteSqlitePlugin,
  type ExpoSqliteLike,
} from "@-/com.npmjs.rozenite__sqlite-plugin";
import {
  useActorDatabaseGeneration,
  useGetActorDatabaseConnection,
} from "@-/dev.improve.haniverse.contexts.database.actor";
import { useGetContentDatabaseConnection } from "@-/dev.improve.haniverse.contexts.database.content";
import {
  useGetStaticDatabaseConnection,
  useStaticDatabaseGeneration,
} from "@-/dev.improve.haniverse.contexts.database.static";
import { useEffect, useMemo, useState } from "react";
import { isRozeniteNativeDevtoolsAvailable } from "#is-rozenite-native-devtools-available";
import { hasLength } from "@-/com.npmjs.ts-array-length";

type ExpoSqliteDatabaseEntry = {
  database: ExpoSqliteLike;
  name: string;
};

function isExpoSqliteLike(value: unknown): value is ExpoSqliteLike {
  return (
    typeof value === "object" &&
    value !== null &&
    "getAllAsync" in value &&
    "runAsync" in value &&
    typeof value.getAllAsync === "function" &&
    typeof value.runAsync === "function"
  );
}

/** Registers the Rozenite SQLite plugin when the FuseBox bridge is present at first mount. */
export function useAppDatabasesRozeniteSqlite() {
  const [enabled] = useState(isRozeniteNativeDevtoolsAvailable);
  const actorGeneration = useActorDatabaseGeneration();
  const staticGeneration = useStaticDatabaseGeneration();
  const getActorConnection = useGetActorDatabaseConnection();
  const getContentConnection = useGetContentDatabaseConnection();
  const getStaticConnection = useGetStaticDatabaseConnection();

  const [databases, setDatabases] = useState<Record<string, ExpoSqliteDatabaseEntry>>({});

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;

    void (async () => {
      const next: Record<string, ExpoSqliteDatabaseEntry> = {};

      const actor = await getActorConnection();
      if (isExpoSqliteLike(actor)) {
        next.actor = { database: actor, name: "actor.db" };
      }

      // Content/static are remote (worker/D1) on web — only register local expo-sqlite handles.
      const content = await getContentConnection();
      if (isExpoSqliteLike(content)) {
        next.content = { database: content, name: "content.db" };
      }

      const staticConnection = await getStaticConnection();
      if (isExpoSqliteLike(staticConnection)) {
        next.static = { database: staticConnection, name: "static.db" };
      }

      if (!cancelled) {
        setDatabases(next);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    actorGeneration,
    enabled,
    getActorConnection,
    getContentConnection,
    getStaticConnection,
    staticGeneration,
  ]);

  const adapters = useMemo(() => {
    if (hasLength(Object.keys(databases), 0)) {
      return [];
    }

    return [
      createExpoSqliteAdapter({
        adapterName: "Expo SQLite",
        databases,
      }),
    ];
  }, [databases]);

  if (!enabled) {
    return;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks -- `enabled` is fixed for this mount via useState
  useRozeniteSqlitePlugin({ adapters });
}
